import styled from 'styled-components';
import { useRef, useState, ChangeEvent } from 'react';
import XLSX from 'xlsx';

import Message from 'modules/Common/Components/Message';
import FooterActionButton from 'modules/Campaigns/CampaignAssets/Components/FooterActionButton';
import UploadIcon from '../../../../assets/img/CuratedContent/Uploadfileicon.svg';
import { GLOBAL_ACTION } from 'modules/GlobalActions';
import store from 'store';

interface Props {
  setShowImportListAsider: any;
  setShowImportListMapAsider: any;
  fileName: string;
  setFileName: any;
  fieldsMapping?: any;
  setFieldsMapping?: any;
  nonMappedFields?: string[];
  setNonMappedFields?: any;
  selectedFile: File;
  setSelectedFile: any;
}

const ImportList: React.FC<Props> = ({
  setShowImportListAsider,
  setShowImportListMapAsider,
  fileName,
  setFileName,
  fieldsMapping,
  setFieldsMapping,
  nonMappedFields,
  setNonMappedFields,
  selectedFile,
  setSelectedFile,
}) => {
  const inputFileEL = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState({
    status: false,
    message: '',
    type: 'error',
    label: '',
  });
  let fileReader: FileReader;

  const triggerInputClick = () => {
    inputFileEL?.current?.click();
  };

  const onFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList) return;
    let file = fileList[0];
    e.target.value = '';
    setSelectedFile(file);
    validateFile(file);
  };

  const onFileDrop = (e: any) => {
    e.preventDefault();
    const fileList = e.dataTransfer.files;
    let file = fileList[0];
    setSelectedFile(file);
    validateFile(file);
  };

  const onDragOver = (e: any) => {
    e.preventDefault();
  };

  const onDragEnter = (e: any) => {
    e.preventDefault();
  };

  const getFileExtensionFromType = (type: string) => {
    return type.slice(((type.lastIndexOf('/') - 1) >>> 0) + 2);
  };

  const getFileExtension = (filename: string) => {
    return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
  };

  const validateFile = (file: File) => {
    const fileExtension = (
      getFileExtension(file.name) ||
      getFileExtensionFromType(file.type) || // Using file type if not in file name
      file.name
    ).toLowerCase();
    if (file.name.includes(',')) {
      setFileName(file.name.replaceAll(',', ' '));
    } else {
      setFileName(file.name);
    }
    const allowedExtensions = ['csv', 'xlsx'];
    if (allowedExtensions.indexOf(fileExtension) >= 0) {
      if (file.size / 1024 > 102400) {
        setMessage({
          status: true,
          message: 'File must be less than 100 MB',
          type: 'error',
          label: '',
        });
        return;
      }
    } else {
      setMessage({
        status: true,
        message: 'Not a valid file type. Allowed types are ' + allowedExtensions.join(', '),
        type: 'error',
        label: '',
      });
      return;
    }
    setMessage({
      status: false,
      message: '',
      type: 'error',
      label: '',
    });
  };

  const processFile = () => {
    fileReader = new FileReader();
    if (selectedFile.name.split('.').pop()?.toLowerCase() === 'csv') {
      fileReader.onloadend = handleCSVRead;
      fileReader.readAsText(selectedFile);
    } else {
      fileReader.onloadend = handleExcelRead;
      fileReader.readAsBinaryString(selectedFile);
    }
    store.dispatch({
      type: GLOBAL_ACTION.IMPORT_LIST_POPOVER,
      payload: { importListAsiderState: false },
    });
  };

  const handleCSVRead = () => {
    let content = fileReader.result || '';
    if (typeof content === 'string') {
      let header = content?.split('\n')[0];
      if (header && header !== '') {
        let fieldsList = header.split(',');
        generateMapping(fieldsList);
      }
    }
    setShowImportListAsider(false);
    setShowImportListMapAsider(true);
  };

  const handleExcelRead = () => {
    let content = fileReader.result || '';
    let workbook = XLSX.read(content, { type: 'binary', sheetRows: 1 });
    let first_worksheet = workbook.Sheets[workbook.SheetNames[0]];
    let header_row: any = XLSX.utils.sheet_to_json(first_worksheet, { header: 1 });
    generateMapping(header_row[0]);
    setShowImportListAsider(false);
    setShowImportListMapAsider(true);
  };

  const generateMapping = (fieldsList: any) => {
    let firstNamePatterns = ['first name', 'fname'];
    let lastNamePatterns = ['last name', 'lname'];
    let emailPatterns = ['email', 'email address'];
    let designationPatterns = ['designation', 'job title'];
    let companyNamePatterns = ['company', 'company name', 'organization'];
    let companyWebsitePatterns = ['website', 'company website'];
    let phoneNumberPatterns = ['phone number', 'phone number'];
    let newFieldsMapping = {
      first_name: '0',
      last_name: '0',
      email: '0',
      designation: '0',
      company_name: '0',
      company_website: '0',
      phone_number: '0',
    };
    let newNotMappedFields = Array<string>();
    for (let label of fieldsList) {
      if (firstNamePatterns.indexOf(label?.toLowerCase()) >= 0 && newFieldsMapping['first_name'] == '0') {
        newFieldsMapping['first_name'] = label;
      } else if (lastNamePatterns.indexOf(label?.toLowerCase()) >= 0 && newFieldsMapping['last_name'] == '0') {
        newFieldsMapping['last_name'] = label;
      } else if (emailPatterns.indexOf(label?.toLowerCase()) >= 0 && newFieldsMapping['email'] == '0') {
        newFieldsMapping['email'] = label;
      } else if (designationPatterns.indexOf(label?.toLowerCase()) >= 0 && newFieldsMapping['designation'] == '0') {
        newFieldsMapping['designation'] = label;
      } else if (companyNamePatterns.indexOf(label?.toLowerCase()) >= 0 && newFieldsMapping['company_name'] == '0') {
        newFieldsMapping['company_name'] = label;
      } else if (companyWebsitePatterns.indexOf(label?.toLowerCase()) >= 0 && newFieldsMapping['company_website'] == '0') {
        newFieldsMapping['company_website'] = label;
      } else if (phoneNumberPatterns.indexOf(label?.toLowerCase()) >= 0 && newFieldsMapping['phone_number'] == '0') {
        newFieldsMapping['phone_number'] = label;
      } else {
        newNotMappedFields.push(label);
      }
    }
    setFieldsMapping(newFieldsMapping);
    setNonMappedFields(newNotMappedFields);
  };

  return (
    <>
      {message.message !== '' ? <Message type={message.type} label={message.label} message={message.message} /> : null}
      <p style={{ margin: '5px 0px' }}>{fileName ? 'Selected file: ' + fileName : ''}</p>
      <Wrapper onClick={triggerInputClick} onDragEnter={onDragEnter} onDragOver={onDragOver} onDrop={onFileDrop} id="drop-file">
        <div>
          <img src={UploadIcon} alt="upload icon" />
          <h4>
            Drop file <br /> here to upload.
          </h4>
          <input type="file" style={{ display: 'none' }} ref={inputFileEL} onChange={onFileSelect} id="drop-file-input" />
        </div>
      </Wrapper>
      <FooterActionButton>
        <span className={`footer-btn filled-success ${message.message === '' && fileName !== '' ? '' : 'disabled'}`} onClick={processFile}>
          Proceed
        </span>
      </FooterActionButton>
    </>
  );
};

const Wrapper = styled.div<{ isDisabled?: boolean }>`
  cursor: pointer;
  padding: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  ${(props) => (props.isDisabled ? 'pointer-events:none;' : '')}
  background-image: linear-gradient(
    180deg,
    rgba(217, 217, 217, 0.6),
    rgba(232, 232, 232, 0.26)
  );
  border: dashed 2px;
  border-color: #c4c4c4;
  border-radius: 8px;
  img {
    max-width: 50px;
    margin-right: 18px;
  }
  h4 {
    color: #424b50;
    font-size: 20px;
    font-family: 'Roboto Slab', serif;
    margin: 0;
    font-weight: 600;
    opacity: 52%;
    padding: 0;
  }
  .loader-container {
    position: absolute;
  }
  .disabled {
    opacity: 0.5;
  }
`;

export default ImportList;
